import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEOSAuth } from "../../contexts/EOSAuthContext";

export type HeaderVariant = "default" | "public";

export type NavItem = {
  label: string;
  path: string;
};

export const navItems: NavItem[] = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Broker", path: "/broker-selection" },
  { label: "Models", path: "/models" },
  { label: "Trades", path: "/trades" },
];

export function useHeader(variant: HeaderVariant, hasStickyBelow: boolean) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useEOSAuth();

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [fixedStackHeight, setFixedStackHeight] = useState(80);

  const profileDropdownRef = useRef<HTMLDivElement | null>(null);
  const fixedStackRef = useRef<HTMLDivElement | null>(null);

  const isPublicRoute = location.pathname === "/" || location.pathname === "/login";
  const showPublicHeader = variant === "public" || isPublicRoute;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let frame: number | null = null;
    const handleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 24);
        const max = document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(max > 0 ? Math.min(100, (y / max) * 100) : 0);
        frame = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Header and the optional sticky ticker render inside one fixed wrapper, so
  // measuring that single wrapper (instead of each piece separately) means the
  // reserved spacer height can never drift out of sync with what's actually
  // pinned to the top of the viewport.
  useEffect(() => {
    if (!fixedStackRef.current) return;

    const el = fixedStackRef.current;
    const updateHeight = () => setFixedStackHeight(el.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasStickyBelow]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      // noop
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActiveRoute = (route: string) => location.pathname === route;

  const getNavButtonClasses = (route: string) => {
    const baseClasses = "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200";
    return isActiveRoute(route)
      ? `${baseClasses} bg-white/10 text-accent-300`
      : `${baseClasses} text-slate-300 hover:text-accent-300 hover:bg-white/5`;
  };

  return {
    user,
    location,
    showPublicHeader,
    profileDropdownOpen,
    setProfileDropdownOpen,
    mobileMenuOpen,
    setMobileMenuOpen,
    scrolled,
    scrollProgress,
    fixedStackHeight,
    profileDropdownRef,
    fixedStackRef,
    isActiveRoute,
    getNavButtonClasses,
    handleLogout,
    handleNavigation,
  };
}
